import React, { useEffect, useState, useMemo } from "react";
import { Tabs, Spin } from "antd";
import { EditOutlined, UserOutlined } from "@ant-design/icons";
import { useLocation, useParams } from "react-router-dom";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import styled from "styled-components";
import { CALL_API } from "common/API";
import DocumentTab from "./DocumentTab";
import ClientTaskTab from "./ClientTaskTab";
import BillsModule from "./Billing";
import Transcation from "./Tranasacation";
import FundReport from "../FundReport";
import PersonIcon from "../../assets/personIconClient.svg";
import CompanyIcon from "../../assets/companyIconClient.svg";

const BreadcrumbRow = styled.div`
  font-size: 16px;
  color: #888;
  padding: 0px 0px 14px 0px;
  display: flex;
  align-items: center;
  gap: 10px;
  .link {
    color: #888;
    cursor: pointer;
  }
  .sep {
    color: #bbb;
  }
  .current {
    color: #222;
  }
`;

const HeaderCard = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  ${'' /* padding: 16px 24px 20px; */}
`;

const AvatarBox = styled.div`
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 80px;
    height: 80px;
    object-fit: contain;
    display: block;
  }
`;

const ClientMeta = styled.div`
  h2 {
    font-size: 14px;
    font-weight: 700;
    margin: 0 0 2px 0;
    color: #111;
  }
  span {
    font-size: 15px;
    color: #4d4c4c;
  }
`;

const VDivider = styled.div`
  width: 1px;
  height: 36px;
  background: #e0e0e0;
  margin: 0 10px;
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  .value {
    font-size: 15px;
    font-weight: 600;
    color: ${(p) => (p.accent ? "#111" : "#111")};
  }
  .label {
    font-size: 15px;
    color: #494949;
  }
`;

const TabsWrap = styled.div`
  padding: 24px 0px;
  border-bottom: 1px solid #ebebeb;

  .ant-tabs-nav {
    margin-bottom: 0 !important;
  }
  .ant-tabs-nav-list {
    gap: 20px;
  }
  .ant-tabs-tab {
    font-size: 16px;
    color: #888888;
    padding: 10px 0 !important;
    margin: 0 20px 0 0 !important;
    font-weight: 600;
  }
  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: #111 !important;
    font-weight: 600;
  }
  .ant-tabs-ink-bar {
    background: #111 !important;
  }
`;

const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #111;
  margin: 0 0 10px 0;
`;

const EditBtn = styled.div`
  position: absolute;
  top: 24px;
  right: 0px;
  width: 35px;
  height: 35px;
  border-radius: 6px;
  background: #deefee;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #00685F;
  font-size: 13px;
  ${'' /* &:hover {
    opacity: 0.75;
  } */}
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr 180px 1fr;
  margin-bottom: 20px;
`;

const InfoLabel = styled.div`
  font-size: 14px;
  color: ${(p) => (p.blue ? "#838485" : "#999797")};
  padding: 9px 0;
`;

const InfoValue = styled.div`
  font-size: 14px;
  color: #222;
  padding: 9px 12px 9px 0;

  a {
    color: #115af7;
    text-decoration: none;
    word-break: break-all;
  }
`;

const PersonTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin: 14px 0 6px 0;
`;

// ⭐ Shared wrapper so every tab's content starts with the same top
// spacing that the Billing tab uses (padding-top: 24). Reused here
// instead of duplicating the inline style object on each tab.
const TabContent = styled.div`
  padding-top: 24px;
`;

const Field = ({ label, value, isLink, blue }) => (
	<>
		<InfoLabel blue={blue}>{label}</InfoLabel>
		<InfoValue>
			{isLink && value ? (
				<a href={value} target="_blank" rel="noreferrer">
					{value}
				</a>
			) : (
				value || "—"
			)}
		</InfoValue>
	</>
);

const ClientDetail = () => {
	const location = useLocation();
	const history = useHistory();
	const { id } = useParams();

	const [clientData, setClientData] = useState(
		location?.state?.clientData || null
	);
	const [loading, setLoading] = useState(!location?.state?.clientData);
	const [activeTab, setActiveTab] = useState("info");

	// ⭐ Lifted from FundRequestTab so ClientDetail knows when the Fund
	// Request tab has drilled into "Send" or "Mark as Paid", and can hide
	// the breadcrumb/header/tabs while those sub-views are showing.
	const [fundMarkAsPaidRecord, setFundMarkAsPaidRecord] = useState(null);
	const [fundSendRecord, setFundSendRecord] = useState(null);
	const isFundSubView = !!fundMarkAsPaidRecord || !!fundSendRecord;

	// Always re-fetch the full record by id. The row object passed via router
	// state may be a reduced shape (e.g. coming from a filtered client list,
	// whose endpoint only returns a subset of fields), so we don't rely on it.
	useEffect(() => {
		if (id) fetchClient();
	}, [id]);

	const fetchClient = async () => {
		try {
			if (!clientData) setLoading(true);
			const response = await CALL_API(
				`/insyts-legal/getlegalclientbyid?clientid=${id}`,
				"get"
			);
			if (response?.code === "SUCCESS" && response?.data) {
				setClientData(response.data);
			}
		} catch (e) {
			console.error(e);
		} finally {
			setLoading(false);
		}
	};

	const handleEdit = () => {
		history.push({
			pathname: `/client-new/${clientData?.bdo_id}`,
			state: { editData: clientData, editMode: true },
		});
	};

	if (loading) {
		return (
			<div
				style={{ display: "flex", justifyContent: "center", paddingTop: 80 }}
			>
				<Spin size="large" />
			</div>
		);
	}

	if (!clientData) {
		return (
			<div style={{ padding: 32, color: "#888", fontSize: 13 }}>
				Client not found.
			</div>
		);
	}

	const isCompany =
		clientData.clienttype === "company" || clientData.clientType === "company";

	const displayName = isCompany
		? clientData.companyregisteredname || clientData.clientName
		: clientData.clientname || clientData.clientName;

	const personnel = Array.isArray(clientData.responsiblepersonnel)
		? clientData.responsiblepersonnel
		: [];

	// ⭐ Client Information tab now uses the same TabContent wrapper (and
	// therefore the same padding-top: 24 spacing) as every other tab.
	// EditBtn is position: absolute, so we add position: relative inline
	// here rather than baking it into the shared TabContent styled-component.
	const ClientInfoTab = (
		<TabContent style={{ position: "relative" }}>
			<EditBtn onClick={handleEdit}>
				<EditOutlined />
			</EditBtn>

			{/* General Information */}
			<div></div>
			<SectionTitle>General Information</SectionTitle>
			<InfoGrid>
				<Field
					label="Official Email"
					value={clientData.officialemail || clientData.email}
				/>
				<Field label="Phone" value={clientData.phone} />
				{isCompany && (
					<>
						<Field
							label="Primary Industry"
							value={clientData.primaryindustry || clientData.industry}
						/>
						<Field
							label="Company URL"
							value={clientData.companywebsiteurl || clientData.website}
							blue
							isLink
						/>
					</>
				)}
				<Field
					label="Primary Contact Person"
					value={clientData.primarycontactperson || clientData.primaryContact}
				/>
				<Field
					label="Primary Contact Email"
					value={clientData.primarycontactemail || clientData.primaryEmail}
				/>
			</InfoGrid>

			{/* Address */}
			<SectionTitle>Address</SectionTitle>
			<InfoGrid>
				<Field
					label="Registered Address"
					value={clientData.registeredaddress || clientData.registeredAddress}
				/>
				<Field label="City" value={clientData.city} />
				<Field label="State/Province" value={clientData.state} />
				<Field label="Zip/Postal code" value={clientData.zipcode} />
				<Field
					label="Country"
					value={clientData.country || clientData.managingoffice}
				/>
				<InfoLabel />
				<InfoValue />
			</InfoGrid>

			{isCompany && (
				<>
					<SectionTitle>
						Other Responsible Personnel in the Organization
					</SectionTitle>
					{personnel.length === 0 ? (
						<div style={{ color: "#bbb", fontSize: 13 }}>
							No personnel added.
						</div>
					) : (
						personnel.map((person, idx) => (
							<div key={idx}>
								<PersonTitle>Person {idx + 1}</PersonTitle>
								<InfoGrid>
									<Field label="Name of the person" value={person.name} />
									<Field
										label="Role / Designation"
										value={person.designation}
									/>
									<Field label="Email" value={person.email} />
									<Field label="Phone" value={person.phone} />
								</InfoGrid>
							</div>
						))
					)}
				</>
			)}
		</TabContent>
	);

	const tabItems = [
		{ key: "info", label: "Client Information", children: ClientInfoTab },
		{
			key: "document",
			label: "Document",
			children: (
				<TabContent>
					<DocumentTab clientId={clientData?.bdo_id} activeTab={activeTab} />
				</TabContent>
			),
		},
		{
			key: "billing",
			label: "Billing",
			children: (
				<TabContent>
					<BillsModule clientData={clientData} />
				</TabContent>
			),
		},
		{
			key: "task",
			label: "Task",
			children: (
				<TabContent>
					<ClientTaskTab clientId={clientData?.bdo_id} />
				</TabContent>
			),
		},
		{
			key: "fund",
			label: "Fund Request",
			children: (
				<TabContent>
					<FundReport
						page="client"
						markAsPaidRecord={fundMarkAsPaidRecord}
						onMarkAsPaidChange={setFundMarkAsPaidRecord}
						sendRecord={fundSendRecord}
						onSendRecordChange={setFundSendRecord}
					/>
				</TabContent>
			),
		},
		{
			key: "transaction",
			label: "Transaction",
			children: (
				<TabContent>
					<Transcation clientId={clientData?.bdo_id} clientData={clientData} module="client" />
				</TabContent>
			),
		},
	];

	return (
		<div style={{ background: "#fff", minHeight: "100vh" }}>
			{/* ⭐ Breadcrumb + header + tabs are hidden while the Fund Request
			    tab is showing its "Send" or "Mark as Paid" sub-view, since
			    those sub-views render their own "Back" breadcrumb. */}
			{!isFundSubView && (
				<>
					<BreadcrumbRow>
						<span className="link" onClick={() => history.push("/client")}>
							Client
						</span>
						<span className="sep">›</span>
						<span className="current">{displayName}</span>
					</BreadcrumbRow>

					{/* Header */}
					<HeaderCard>
						<AvatarBox>
							<img
								src={isCompany ? CompanyIcon : PersonIcon}
								alt={isCompany ? "Company" : "Person"}
							/>
						</AvatarBox>

						<ClientMeta>
							<h2>{displayName}</h2>
							<span>{isCompany ? "Company" : "Person"}</span>
						</ClientMeta>

						<VDivider />

						<MetaItem>
							<span className="value">{clientData.bdo_id || "—"}</span>
							<span className="label">Client ID</span>
						</MetaItem>

						<VDivider />
						{clientData?.clienttype === "company" &&

							<MetaItem em accent>
								<span className="value" style={{ fontWeight: "700" }}>
									{clientData.owner || clientData.createdBy || "—"}
								</span>
								<span className="label">Owner</span>
							</MetaItem>
						}
					</HeaderCard>
				</>
			)}

			{/* Tabs */}
			{isFundSubView ? (
				// Fund Request drilled into Send / Mark as Paid — render just
				// that sub-view, full width, without the tab strip. Wrapped in
				// TabContent so it shares the same top spacing as every other tab.
				<TabContent>
					<FundReport
						page="client"
						markAsPaidRecord={fundMarkAsPaidRecord}
						onMarkAsPaidChange={setFundMarkAsPaidRecord}
						sendRecord={fundSendRecord}
						onSendRecordChange={setFundSendRecord}
					/>
				</TabContent>
			) : (
				<TabsWrap>
					<Tabs items={tabItems} defaultActiveKey="info" onChange={(key) => setActiveTab(key)} />
				</TabsWrap>
			)}
		</div>
	);
};

export default ClientDetail;